import React, { useCallback, useRef } from 'react';
import { ACTIONTYPE } from '../AppStateProvider';
import axios from 'axios';
import { PreflightTest, runPreflight } from 'twilio-video';

// Safety timeout: 30s (15s test + 15s buffer for network overhead)
const PREFLIGHT_SAFETY_TIMEOUT_MS = 30_000;

export default function usePreflightTest(dispatch: React.Dispatch<ACTIONTYPE>) {
  const preflightTestRef = useRef<PreflightTest>();
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const startPreflightTest = useCallback(() => {
    // Don't start a new preflight test if one is already running
    if (preflightTestRef.current) {
      return;
    }

    dispatch({ type: 'preflight-started' });

    return axios('app/token')
      .then((response) => {
        // Run for 15 seconds instead of the default 10s to get more samples
        // and reduce the impact of startup/teardown packet loss artifacts.
        const preflightTest = runPreflight(response.data.token, { duration: 15000 });

        preflightTestRef.current = preflightTest;

        // Per-test safety timeout
        safetyTimeoutRef.current = setTimeout(() => {
          if (preflightTestRef.current) {
            console.warn('Preflight test safety timeout: auto-stopping after', PREFLIGHT_SAFETY_TIMEOUT_MS, 'ms');
            preflightTestRef.current.stop();
            preflightTestRef.current = undefined;
            dispatch({ type: 'preflight-failed', error: new Error('Preflight test timed out') });
            dispatch({ type: 'preflight-finished' });
          }
        }, PREFLIGHT_SAFETY_TIMEOUT_MS);

        preflightTest.on('progress', (progress) => {
          dispatch({ type: 'preflight-progress', progress });
        });

        preflightTest.on('completed', (report) => {
          if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
          preflightTestRef.current = undefined;
          dispatch({ type: 'preflight-completed', report });
          dispatch({ type: 'preflight-finished' });
        });

        preflightTest.on('failed', (error) => {
          if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
          preflightTestRef.current = undefined;
          dispatch({ type: 'preflight-failed', error });
          dispatch({ type: 'preflight-finished' });
        });
      })
      .catch((error) => {
        console.error('Error running the preflight test', error);
        if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
        dispatch({ type: 'preflight-token-failed', error });
        dispatch({ type: 'preflight-finished' });
      });
  }, [dispatch]);

  const cancelPreflightTest = useCallback(() => {
    // Stop the preflight test if running
    if (preflightTestRef.current) {
      preflightTestRef.current.stop();
      preflightTestRef.current = undefined;
    }

    if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);

    // Mark test as finished
    dispatch({ type: 'preflight-finished' });
  }, [dispatch]);

  return { startPreflightTest, cancelPreflightTest } as const;
}
